import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { seedDemoData, clearDemoData, getDemoStats, SeedResults } from '@/utils/seedDemoData';
import { toast } from 'sonner';
import { Database, Trash2, Play, Info, RefreshCw } from 'lucide-react';

const DemoDataPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [teamsPerDivision, setTeamsPerDivision] = useState(4);
  const [selectedDivisions, setSelectedDivisions] = useState(['13U', '14U', '15U', '16U']);
  const [results, setResults] = useState<SeedResults | null>(null);
  const [stats, setStats] = useState<{ teams: number; registrations: number } | null>(null);

  const allDivisions = ['10U', '11U', '12U', '13U', '14U', '15U', '16U', '17U'];

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    const currentStats = await getDemoStats();
    setStats(currentStats);
  };

  const handleSeed = async () => {
    setLoading(true);
    try {
      const result = await seedDemoData({
        teamsPerDivision,
        divisions: selectedDivisions
      });
      setResults(result);
      await loadStats();
      if (result.errors.length === 0) {
        toast.success(`Created ${result.teams} teams and ${result.registrations} registrations`);
      } else {
        toast.warning(`Completed with ${result.errors.length} errors`);
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    if (!confirm('Are you sure you want to delete all demo data?')) return;
    setLoading(true);
    try {
      await clearDemoData();
      setResults(null);
      await loadStats();
      toast.success('Demo data cleared');
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleDivision = (div: string) => {
    setSelectedDivisions(prev =>
      prev.includes(div) ? prev.filter(d => d !== div) : [...prev, div]
    );
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <Database className="w-6 h-6" />
        Demo Data Management
      </h1>

      {/* Current Stats */}
      <Card className="mb-6 bg-muted/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="w-4 h-4" />
            Current Demo Data
            <Button variant="ghost" size="sm" onClick={loadStats} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-6 text-sm">
            <div>
              <span className="font-semibold">{stats?.teams || 0}</span> Teams
            </div>
            <div>
              <span className="font-semibold">{stats?.registrations || 0}</span> Registrations
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Seed Demo Data</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Teams per Division</Label>
            <Input
              type="number"
              value={teamsPerDivision}
              onChange={(e) => setTeamsPerDivision(parseInt(e.target.value) || 4)}
              min={1}
              max={10}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Total teams: {teamsPerDivision * selectedDivisions.length}
            </p>
          </div>

          <div>
            <Label className="mb-2 block">Divisions</Label>
            <div className="flex flex-wrap gap-3">
              {allDivisions.map(div => (
                <label key={div} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedDivisions.includes(div)}
                    onCheckedChange={() => toggleDivision(div)}
                  />
                  <span className="text-sm">{div}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={handleSeed} disabled={loading || selectedDivisions.length === 0}>
              <Play className="w-4 h-4 mr-2" />
              {loading ? 'Seeding...' : 'Seed Demo Data'}
            </Button>
            <Button variant="destructive" onClick={handleClear} disabled={loading}>
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All Demo Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {results && (
        <Card>
          <CardHeader>
            <CardTitle>Seeding Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-2xl font-bold">{results.coaches}</div>
                <div className="text-sm text-muted-foreground">Coaches Created</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{results.teams}</div>
                <div className="text-sm text-muted-foreground">Teams Created</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{results.registrations}</div>
                <div className="text-sm text-muted-foreground">Registrations Created</div>
              </div>
            </div>
            {results.errors.length > 0 && (
              <div className="mt-4 p-3 bg-destructive/10 rounded text-sm text-destructive">
                <strong>Errors:</strong>
                <ul className="list-disc list-inside mt-1">
                  {results.errors.map((err: string, i: number) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DemoDataPage;
